import { useState, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation, useMatch, useOutlet } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import { isWebhookPanelPersistent, subscribeToWebhookPanelPersistence } from '../utils/webhookPanelPersistence';
import { isSmtpPanelPersistent, subscribeToSmtpPanelPersistence } from '../utils/smtpPanelPersistence';
import { isSuppressionPanelPersistent, subscribeToSuppressionPanelPersistence } from '../utils/suppressionPanelPersistence';

export default function AuthenticatedOutlet() {
  const location = useLocation();
  const outlet = useOutlet();
  const isWebhooksRoute = location.pathname.replace(/\/+$/, '') === '/webhooks';
  const isSuppressionRoute = location.pathname.replace(/\/+$/, '') === '/contacts/suppressions';
  const isSmtpRoute = location.pathname.replace(/\/+$/, '') === '/smtp';
  const webhookPanelOpen = useSyncExternalStore(
    subscribeToWebhookPanelPersistence,
    isWebhookPanelPersistent,
    isWebhookPanelPersistent,
  );
  const [webhookRouteCache, setWebhookRouteCache] = useState<{ locationKey: string; outlet: ReactNode } | null>(null);
  if (isWebhooksRoute && !webhookPanelOpen && webhookRouteCache?.locationKey !== location.key) {
    setWebhookRouteCache({ locationKey: location.key, outlet });
  } else if (!isWebhooksRoute && !webhookPanelOpen && webhookRouteCache !== null) {
    setWebhookRouteCache(null);
  }
  const cachedWebhookOutlet = webhookRouteCache?.outlet ?? null;
  const displayedWebhookOutlet = isWebhooksRoute
    ? (webhookPanelOpen ? cachedWebhookOutlet ?? outlet : outlet)
    : cachedWebhookOutlet;

  const smtpPanelOpen = useSyncExternalStore(
    subscribeToSmtpPanelPersistence,
    isSmtpPanelPersistent,
    isSmtpPanelPersistent,
  );
  const [smtpRouteCache, setSmtpRouteCache] = useState<{ locationKey: string; outlet: ReactNode } | null>(null);
  if (isSmtpRoute && !smtpPanelOpen && smtpRouteCache?.locationKey !== location.key) {
    setSmtpRouteCache({ locationKey: location.key, outlet });
  } else if (!isSmtpRoute && !smtpPanelOpen && smtpRouteCache !== null) {
    setSmtpRouteCache(null);
  }
  const cachedSmtpOutlet = smtpRouteCache?.outlet ?? null;
  const displayedSmtpOutlet = isSmtpRoute
    ? (smtpPanelOpen ? cachedSmtpOutlet ?? outlet : outlet)
    : cachedSmtpOutlet;

  const suppressionPanelOpen = useSyncExternalStore(
    subscribeToSuppressionPanelPersistence,
    isSuppressionPanelPersistent,
    isSuppressionPanelPersistent,
  );
  const [suppressionRouteCache, setSuppressionRouteCache] = useState<{ locationKey: string; outlet: ReactNode } | null>(null);
  if (isSuppressionRoute && !suppressionPanelOpen && suppressionRouteCache?.locationKey !== location.key) {
    setSuppressionRouteCache({ locationKey: location.key, outlet });
  } else if (!isSuppressionRoute && !suppressionPanelOpen && suppressionRouteCache !== null) {
    setSuppressionRouteCache(null);
  }
  const cachedSuppressionOutlet = suppressionRouteCache?.outlet ?? null;
  const displayedSuppressionOutlet = isSuppressionRoute
    ? (suppressionPanelOpen ? cachedSuppressionOutlet ?? outlet : outlet)
    : cachedSuppressionOutlet;

  const editor = useMatch('/templates/:templateId/:mode?');
  // Editor tabs share one draft. Other navigation retains the existing remount behavior.
  const key = editor ? `template:${editor.params.templateId}` : location.key;
  return (
    <>
      {(isWebhooksRoute || (webhookPanelOpen && cachedWebhookOutlet !== null)) && displayedWebhookOutlet && (
        <div className={isWebhooksRoute ? 'contents' : 'hidden'}>
          <ErrorBoundary resetKey={location.key}>{displayedWebhookOutlet}</ErrorBoundary>
        </div>
      )}
      {(isSmtpRoute || (smtpPanelOpen && cachedSmtpOutlet !== null)) && displayedSmtpOutlet && (
        <div className={isSmtpRoute ? 'contents' : 'hidden'}>
          <ErrorBoundary resetKey={location.key}>{displayedSmtpOutlet}</ErrorBoundary>
        </div>
      )}
      {(isSuppressionRoute || (suppressionPanelOpen && cachedSuppressionOutlet !== null)) && displayedSuppressionOutlet && (
        <div className={isSuppressionRoute ? 'contents' : 'hidden'}>
          <ErrorBoundary resetKey={location.key}>{displayedSuppressionOutlet}</ErrorBoundary>
        </div>
      )}
      {!isWebhooksRoute && !isSmtpRoute && !isSuppressionRoute && (
        <ErrorBoundary key={key} resetKey={location.key}>
          {outlet}
        </ErrorBoundary>
      )}
    </>
  );
}
